import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { tipApi, nudgeApi } from '@/services/learningManagementApi';
import { skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type {
  Tip,
  TipRequest,
  TipCategory,
  TipType,
  TipDifficultyLevel,
  TipStatus,
  TipImpactLevel,
  GenerationSource,
  TargetAudience,
  Nudge,
} from '@/types/learningManagement';
import type { Skill } from '@/types/skillKnowledge';

const tipSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  skillId: z.string().min(1, 'Skill ID is required'),
  description: z.string().min(0).max(2000, 'Description must be less than 2000 characters'),
  content: z.string().min(0).max(5000, 'Content must be less than 5000 characters'),
  summary: z.string().max(500, 'Summary must be less than 500 characters').optional(),
  category: z.enum(['DAILY_TIP', 'WEEKLY_TIP', 'MONTHLY_TIP', 'QUICK_ADVICE', 'IN_DEPTH_GUIDE', 'CHECKLIST', 'BEST_PRACTICES', 'TIPS_AND_TRICKS']),
  tipType: z.enum(['PRODUCTIVITY', 'LEARNING_STRATEGY', 'GOAL_ACHIEVEMENT', 'TIME_MANAGEMENT', 'HABIT_FORMATION', 'SKILL_DEVELOPMENT', 'MOTIVATION', 'WELLNESS', 'CAREER_DEVELOPMENT', 'PROBLEM_SOLVING']),
  subCategory: z.string().max(100).optional(),
  status: z.enum(['DRAFT', 'ACTIVE', 'SCHEDULED', 'PAUSED', 'ARCHIVED', 'EXPIRED', 'COMPLETED', 'IN_PROGRESS', 'NOT_STARTED']),
  tags: z.array(z.string().max(50)).optional(),
  difficultyLevel: z.enum(['EASY', 'MODERATE', 'CHALLENGING', 'ADVANCED']),
  associatedNudges: z.array(z.string()),
  implementationTimeMinutes: z.number().min(1).max(1440).optional(),
  impactLevel: z.enum(['LOW', 'MEDIUM', 'HIGH', 'TRANSFORMATIVE']).optional(),
  resources: z.array(z.string()),
  relevanceScore: z.number().optional(),
  targetAudience: z.enum(['ALL_USERS', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'PROFESSIONALS', 'STUDENTS', 'MANAGERS', 'CUSTOM_SEGMENT']).optional(),
  deliverySchedule: z.record(z.string(), z.unknown()).optional(),
  personalizationParams: z.record(z.string(), z.unknown()).optional(),
  tipGenerated: z.enum(['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED']),
  authorInfo: z.object({
    authorId: z.string().optional(),
    authorName: z.string().optional(),
    authorEmail: z.string().email().optional(),
  }).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

type TipFormData = z.infer<typeof tipSchema>;

const CATEGORIES: TipCategory[] = ['DAILY_TIP', 'WEEKLY_TIP', 'MONTHLY_TIP', 'QUICK_ADVICE', 'IN_DEPTH_GUIDE', 'CHECKLIST', 'BEST_PRACTICES', 'TIPS_AND_TRICKS'];
const TIP_TYPES: TipType[] = ['PRODUCTIVITY', 'LEARNING_STRATEGY', 'GOAL_ACHIEVEMENT', 'TIME_MANAGEMENT', 'HABIT_FORMATION', 'SKILL_DEVELOPMENT', 'MOTIVATION', 'WELLNESS', 'CAREER_DEVELOPMENT', 'PROBLEM_SOLVING'];
const DIFFICULTY_LEVELS: TipDifficultyLevel[] = ['EASY', 'MODERATE', 'CHALLENGING', 'ADVANCED'];
const STATUSES: TipStatus[] = ['DRAFT', 'ACTIVE', 'SCHEDULED', 'PAUSED', 'ARCHIVED', 'EXPIRED', 'COMPLETED', 'IN_PROGRESS', 'NOT_STARTED'];
const IMPACT_LEVELS: TipImpactLevel[] = ['LOW', 'MEDIUM', 'HIGH', 'TRANSFORMATIVE'];
const TARGET_AUDIENCES: TargetAudience[] = ['ALL_USERS', 'BEGINNERS', 'INTERMEDIATE', 'ADVANCED', 'PROFESSIONALS', 'STUDENTS', 'MANAGERS', 'CUSTOM_SEGMENT'];
const GENERATION_SOURCES: GenerationSource[] = ['SYSTEM_DEFINED', 'AI_GENERATED', 'USER_CUSTOMIZED'];

export const TipFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Array<{ id?: string; name: string }>>([]);
  const [nudges, setNudges] = useState<Array<{ id?: string; nudgeId?: string; title: string }>>([]);
  const { hasPermission } = useAdminAuth();

  const form = useForm<TipFormData>({
    resolver: zodResolver(tipSchema),
    defaultValues: {
      title: '',
      skillId: '',
      description: '',
      content: '',
      summary: '',
      category: 'DAILY_TIP',
      tipType: 'PRODUCTIVITY',
      subCategory: '',
      difficultyLevel: 'EASY',
      status: 'DRAFT',
      implementationTimeMinutes: undefined,
      impactLevel: undefined,
      tags: [],
      associatedNudges: [],
      resources: [],
      relevanceScore: undefined,
      targetAudience: undefined,
      deliverySchedule: undefined,
      personalizationParams: undefined,
      tipGenerated: 'USER_CUSTOMIZED',
      authorInfo: undefined,
      metadata: undefined,
    },
  });

  // Note: useFieldArray is designed for object arrays, but we're using it for string arrays
  const { fields: resourceFields, append: appendResource, remove: removeResource } = useFieldArray({
    control: form.control,
    // @ts-expect-error - name for array field not in form type
    name: 'resources',
  });

  const { fields: nudgeFields, append: appendNudge, remove: removeNudge } = useFieldArray({
    control: form.control,
    // @ts-expect-error - name for array field not in form type
    name: 'associatedNudges',
  });

  useEffect(() => {
    // Load skills and nudges for dropdowns
    Promise.all([
      skillApi.list({ page: 0, size: 1000 }).then((response) => response.content || []),
      nudgeApi.list({ page: 0, size: 1000 }).then((response) => response.content || []),
    ]).then(([skillsData, nudgesData]) => {
      setSkills((skillsData as Skill[]).map((s) => ({ id: s.id, name: s.name })));
      setNudges((nudgesData as Nudge[]).map((n) => ({ id: n.id, nudgeId: n.nudgeId, title: n.title })));
    }).catch((error: unknown) => {
      console.error('Error loading data:', error);
    });
  }, []);

  // Fetch tip data for edit mode
  const {
    data: _tip,
    isLoading: isLoadingTip,
    error: tipError,
  } = useEntity<Tip>({
    fetchFn: (tipId: string) => tipApi.getById(tipId),
    entityId: id,
    entityName: 'tip',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        skillId: data.skillId || '',
        description: data.description || '',
        content: data.content,
        summary: data.summary || '',
        category: data.category,
        tipType: data.tipType,
        subCategory: data.subCategory || '',
        difficultyLevel: data.difficultyLevel,
        status: data.status,
        implementationTimeMinutes: data.implementationTimeMinutes,
        impactLevel: data.impactLevel,
        tags: data.tags || [],
        associatedNudges: data.associatedNudges || [],
        resources: data.resources || [],
        relevanceScore: data.relevanceScore,
        targetAudience: data.targetAudience,
        deliverySchedule: data.deliverySchedule,
        personalizationParams: data.personalizationParams,
        tipGenerated: data.tipGenerated,
        authorInfo: data.authorInfo,
        metadata: data.metadata,
      } as Record<string, unknown>);
    },
    onError: () => {
      navigate('/admin/tips');
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: TipFormData) => {
      const payload: TipRequest = {
        ...data,
        tags: data.tags || [],
      };
      return await tipApi.create(payload);
    },
    entityName: 'tip',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/tips');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: TipFormData) => {
      if (!id) throw new Error('Tip ID is required');
      const payload: TipRequest = {
        ...data,
        tags: data.tags || [],
      };
      return await tipApi.update(id, payload);
    },
    entityName: 'tip',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/tips');
    },
  });

  const onSubmit = async (data: TipFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingTip;
  const mutation = isEdit ? updateMutation : createMutation;

  const isSystemDefined = form.watch('tipGenerated') === 'SYSTEM_DEFINED';
  const canModifyGeneration = !isEdit || !isSystemDefined || hasPermission('system_admin', 'tip');

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading tip..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && tipError) {
    return <ErrorState error={tipError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/tips">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEdit ? 'Edit Tip' : 'Create Tip'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {isEdit ? 'Update tip information' : 'Create a new tip'}
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for the tip</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter tip title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter tip description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content *</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Enter tip content"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter tip summary (max 500 characters)" {...field} />
                    </FormControl>
                    <FormDescription>Brief summary of the tip</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="skillId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Skill ID *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select skill" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {skills.map((skill) => (
                          <SelectItem key={skill.id} value={skill.id || ''}>
                            {skill.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>Select the skill this tip is associated with</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tipType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tip Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select tip type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIP_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="difficultyLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Difficulty Level *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select difficulty" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DIFFICULTY_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="impactLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Impact Level</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select impact level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {IMPACT_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="subCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sub Category</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter sub category (optional)" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="targetAudience"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Audience</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select target audience" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TARGET_AUDIENCES.map((audience) => (
                            <SelectItem key={audience} value={audience}>
                              {audience.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="implementationTimeMinutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Implementation Time (Minutes)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        placeholder="Enter minutes"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4">
                <div>
                  <Label>Associated Nudges</Label>
                  <div className="space-y-2 mt-2">
                    {nudgeFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Select
                          value={form.watch(`associatedNudges.${index}`)}
                          onValueChange={(value) => {
                            const current = form.getValues('associatedNudges') || [];
                            current[index] = value;
                            form.setValue('associatedNudges', current);
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Select nudge" />
                          </SelectTrigger>
                          <SelectContent>
                            {nudges.map((nudge) => (
                              <SelectItem key={nudge.id || nudge.nudgeId} value={nudge.id || nudge.nudgeId || ''}>
                                {nudge.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="outline" size="icon" onClick={() => removeNudge(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendNudge('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Nudge
                    </Button>
                  </div>
                </div>

                <div>
                  <Label>Resources</Label>
                  <div className="space-y-2 mt-2">
                    {resourceFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <Input
                          placeholder="Enter resource URL or description"
                          value={form.watch(`resources.${index}`) || ''}
                          onChange={(e) => {
                            const current = form.getValues('resources') || [];
                            current[index] = e.target.value;
                            form.setValue('resources', current);
                          }}
                        />
                        <Button type="button" variant="outline" size="icon" onClick={() => removeResource(index)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => appendResource('')}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Resource
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="relevanceScore"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Relevance Score</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          placeholder="Enter relevance score (0-100)"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter tags separated by commas"
                        value={field.value?.join(', ') || ''}
                        onChange={(e) => {
                          const tags = e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean);
                          field.onChange(tags);
                        }}
                      />
                    </FormControl>
                    <FormDescription>Separate multiple tags with commas</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="tipGenerated"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generation Source *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={!canModifyGeneration}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select generation source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {GENERATION_SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {!canModifyGeneration && 'Cannot change generation source for system-defined tips'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/tips">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default TipFormPage;
